import React from "react"
import { useTheme } from "@mui/material"
import styled from "styled-components"
import chroma from "chroma-js"
import useId from "react-use-uuid"
import { scaleLinear, scaleSqrt } from "d3-scale"
import { isValidColor } from "../../utils/color"
import Sector from "../Sector"

type TScaleType = "area" | "linear"
type TBorderStyle = "solid" | "dashed" | "dotted"

interface IGetInnerSector {
  innerValue: number
  containerWidth: number
  fill: string
}

interface ICircleProps {
  /**
   * Border path style
   * @default "solid"
   */
  borderStyle?: TBorderStyle
  /**
   * Path fill colour. Passing a number will use that index from the Gamma UI series palette.
   */
  color: string | number
  /**
   * Additional path props. (for more advanced styling)
   */
  extraProps?: React.HTMLProps<HTMLDivElement>
  /**
   * The container's width and height
   */
  containerWidth: number
  /**
   * Show a small dot to mark the center of the circle
   */
  showCore?: boolean
  /**
   * Max bound for the radius scale. Circle will display at max size if value === maxBound
   * @default 1
   */
  maxBound: number
  /**
   * Min bound for the scale
   * @default 0
   */
  minBound: number
  /**
   * Scale by area or diameter of circle
   * @default "area"
   */
  scaleType?: TScaleType
  /**
   * A value to scale the size by
   * @default 1
   */
  value: number
  /**
   * If set, will show a sector inside, where empty = 0, and full = 1
   * @default 0
   */
  innerValue?: number
}

const Wrap = styled.div<{ size: number }>`
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`

const OuterCircle = styled.div<{
  borderStyle: TBorderStyle
  color: string
  size: number
}>`
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  background: ${({ color }) => chroma(color).alpha(0.5).css()};
  border-radius: 50%;
  border: ${({ borderStyle, color }) => `2px ${borderStyle} ${color}`};
  position: relative;
`

const CircleCore = styled.div<{ color: string }>`
  width: 3px;
  height: 3px;
  background: ${({ color }) => chroma(color).alpha(0.9).css()};
  border-radius: 50%;
`

const getInnerSector = ({
  innerValue,
  containerWidth,
  fill
}: IGetInnerSector) => {
  const startAngle = 0
  const endAngle = innerValue * 360
  return (
    <Sector
      startAngle={startAngle}
      endAngle={endAngle}
      outerRadius={containerWidth * 0.5}
      innerRadius={0}
      color={fill}
      pathOnly={false}
      containerStyles={{ position: "absolute" }}
    />
  )
}

export const Circle = ({
  borderStyle = "solid",
  containerWidth,
  color,
  extraProps = {},
  minBound = 0,
  maxBound = 1,
  value = 1,
  scaleType = "area",
  showCore = true,
  innerValue = 0
}: ICircleProps) => {
  const theme = useTheme()
  const id = useId()

  const stripedFillId = `stripes-${id}`

  if (value < minBound || value > maxBound || minBound >= maxBound) {
    return <div>Invalid bounds. Ensure minBound &lt; value &lt; maxBound </div>
  }

  if (innerValue < 0 || innerValue > 1) {
    return <div>innerValue must be &gt; 0 and &lt;= 1</div>
  }

  const depthScale =
    scaleType === "area"
      ? scaleSqrt([minBound, maxBound], [0, containerWidth])
      : scaleLinear([minBound, maxBound], [0, containerWidth])
  const getScaledSize = (size: number) => depthScale(size)

  // get color from palette
  let activeColor = theme.palette.primary.main
  const seriesColors = theme.series || []
  if (typeof color === "number") {
    activeColor = seriesColors[color % seriesColors.length]
  } else if (color && isValidColor(color)) {
    activeColor = color
  } else if (
    color &&
    /primary|secondary|error|warning|info|spice|success/.test(color) &&
    color in theme.palette
  ) {
    activeColor = theme.palette[color].main
  }

  const sectorNode =
    innerValue > 0 && innerValue <= 1
      ? getInnerSector({
          containerWidth,
          innerValue,
          fill: `url(#${stripedFillId})`
        })
      : null

  const scaledSize = getScaledSize(value)

  return (
    <Wrap size={containerWidth}>
      <OuterCircle
        size={scaledSize}
        color={activeColor}
        borderStyle={borderStyle}
      >
        {innerValue > 0 && innerValue <= 1 ? (
          <Sector
            startAngle={0}
            endAngle={innerValue * 360}
            outerRadius={containerWidth * 0.5}
            innerRadius={0}
            color={`url(#${stripedFillId})`}
            pathOnly={false}
            containerStyles={{ position: "absolute" }}
          />
        ) : (
          ""
        )}
        {showCore ? <CircleCore color={activeColor} /> : ""}
      </OuterCircle>
      <svg style={{ position: "absolute" }}>
        {theme.viz?.SVG.patterns.createStripePattern({
          id: stripedFillId,
          fill: activeColor,
          textureOpacity: 0.2,
          fillOpacity: 0.1
        })}
      </svg>
    </Wrap>
  )
}

export default Circle
